import {makeAutoObservable} from "mobx";
import {endAt, get, orderByKey, query, ref, startAt} from "firebase/database";
import {database} from "../utils/firebase";

const toDayKey = (date = new Date()) => {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, "0");
    const day = `${date.getDate()}`.padStart(2, "0");
    return `${year}-${month}-${day}`;
};

const getRecentDayKeys = (rangeDays) => {
    const result = [];
    const now = new Date();
    for (let i = rangeDays - 1; i >= 0; i -= 1) {
        const date = new Date(now);
        date.setDate(now.getDate() - i);
        result.push(toDayKey(date));
    }
    return result;
};

const getDayKeysForInterval = (startDayKey, endDayKey) => {
    const result = [];
    const date = new Date(`${startDayKey}T12:00:00`);
    const endDate = new Date(`${endDayKey}T12:00:00`);
    while (date <= endDate) {
        result.push(toDayKey(date));
        date.setDate(date.getDate() + 1);
    }
    return result;
};

const ACTION_KEYS = ["like", "repost", "follower", "subscriber", "accepter", "connector", "newPoster", "welcome"];

const getMissingIntervals = (dayKeys, loadedDayKeys) => {
    const intervals = [];
    let startDayKey = null;

    dayKeys.forEach((dayKey, index) => {
        if (!loadedDayKeys.has(dayKey) && startDayKey === null) {
            startDayKey = dayKey;
        }

        if (startDayKey !== null && (loadedDayKeys.has(dayKey) || index === dayKeys.length - 1)) {
            intervals.push({
                startDayKey,
                endDayKey: loadedDayKeys.has(dayKey) ? dayKeys[index - 1] : dayKey,
            });
            startDayKey = null;
        }
    });

    return intervals;
};

export class ActionStatsState {
    rootState = null;
    entriesByDay = {};
    loadedDayKeys = new Set();
    activeUid = null;
    dataLoaded = false;
    loading = false;
    error = "";
    rangeDays = 7;
    requestGeneration = 0;
    requestId = 0;
    pendingRequestCount = 0;
    inFlightRequests = new Map();
    latestRequestByDay = new Map();

    constructor(rootState) {
        makeAutoObservable(this);
        this.rootState = rootState;
    }

    setRangeDays(value) {
        this.rangeDays = value;
    }

    reset() {
        this.requestGeneration += 1;
        this.entriesByDay = {};
        this.loadedDayKeys.clear();
        this.activeUid = null;
        this.dataLoaded = false;
        this.loading = false;
        this.error = "";
        this.pendingRequestCount = 0;
        this.inFlightRequests.clear();
        this.latestRequestByDay.clear();
    }

    prepareUser(uid) {
        if (this.activeUid !== uid) {
            this.reset();
            this.activeUid = uid;
        }
    }

    async loadForUser(uid, {force = false} = {}) {
        if (!uid) {
            this.reset();
            return;
        }

        this.prepareUser(uid);
        const dayKeys = getRecentDayKeys(this.rangeDays);
        const intervals = force
            ? [{startDayKey: dayKeys[0], endDayKey: dayKeys[dayKeys.length - 1]}]
            : getMissingIntervals(dayKeys, this.loadedDayKeys);

        if (intervals.length === 0) {
            this.dataLoaded = true;
            return;
        }

        await Promise.all(intervals.map(({startDayKey, endDayKey}) => this.loadInterval(uid, startDayKey, endDayKey, force)));
    }

    loadInterval(uid, startDayKey, endDayKey, force) {
        const requestKey = `${uid}:${startDayKey}:${endDayKey}:${force}`;
        const existingRequest = this.inFlightRequests.get(requestKey);
        if (existingRequest) {
            return existingRequest;
        }

        const requestGeneration = this.requestGeneration;
        const dayKeys = getDayKeysForInterval(startDayKey, endDayKey);
        const requestId = ++this.requestId;
        dayKeys.forEach((dayKey) => this.latestRequestByDay.set(dayKey, requestId));

        this.pendingRequestCount += 1;
        this.loading = true;
        this.error = "";

        let request;
        request = (async () => {
            try {
                const snapshot = await get(query(
                    ref(database, `userActionStats/${uid}`),
                    orderByKey(),
                    startAt(startDayKey),
                    endAt(endDayKey),
                ));

                if (this.requestGeneration !== requestGeneration || this.activeUid !== uid) {
                    return;
                }

                const entries = snapshot.exists() ? snapshot.val() : {};
                const nextEntriesByDay = {...this.entriesByDay};
                const acceptedDayKeys = dayKeys.filter((dayKey) => this.latestRequestByDay.get(dayKey) === requestId);
                if (force) {
                    acceptedDayKeys.forEach((dayKey) => delete nextEntriesByDay[dayKey]);
                }
                acceptedDayKeys.forEach((dayKey) => {
                    if (Object.prototype.hasOwnProperty.call(entries, dayKey)) {
                        nextEntriesByDay[dayKey] = entries[dayKey];
                    }
                });
                this.entriesByDay = nextEntriesByDay;
                acceptedDayKeys.forEach((dayKey) => this.loadedDayKeys.add(dayKey));
                this.dataLoaded = true;
            } catch (reason) {
                if (this.requestGeneration === requestGeneration && this.activeUid === uid) {
                    console.error(reason);
                    this.error = reason?.message || "Failed to load action statistics.";
                }
            } finally {
                if (this.inFlightRequests.get(requestKey) === request) {
                    this.inFlightRequests.delete(requestKey);
                }
                if (this.requestGeneration === requestGeneration && this.activeUid === uid) {
                    this.pendingRequestCount -= 1;
                    this.loading = this.pendingRequestCount > 0;
                }
            }
        })();
        this.inFlightRequests.set(requestKey, request);
        return request;
    }

    getEntriesForRange() {
        const dayKeys = getRecentDayKeys(this.rangeDays);
        return dayKeys.map((dayKey) => {
            const eventsMap = this.entriesByDay?.[dayKey] || {};
            return {
                dayKey,
                events: Object.values(eventsMap).sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0)),
            };
        });
    }

    getTodayCounts() {
        const todayKey = toDayKey(new Date());
        const events = Object.values(this.entriesByDay?.[todayKey] || {});
        const counts = ACTION_KEYS.reduce((acc, key) => ({...acc, [key]: 0}), {});
        events.forEach((event) => {
            if (event?.action && Object.prototype.hasOwnProperty.call(counts, event.action)) {
                counts[event.action] += 1;
            }
        });
        return counts;
    }

    getDailySeries() {
        return this.getEntriesForRange().map(({dayKey, events}) => {
            const counts = ACTION_KEYS.reduce((acc, key) => ({...acc, [key]: 0}), {});
            events.forEach((event) => {
                if (event?.action && Object.prototype.hasOwnProperty.call(counts, event.action)) {
                    counts[event.action] += 1;
                }
            });
            return {
                dayKey,
                total: events.length,
                ...counts,
            };
        });
    }

    getHourlySeries(dayKey = toDayKey(new Date())) {
        const events = Object.values(this.entriesByDay?.[dayKey] || {});
        const buckets = Array.from({length: 24}, (_, hour) => {
            const counts = ACTION_KEYS.reduce((acc, key) => ({...acc, [key]: 0}), {});
            return {
                hour: `${`${hour}`.padStart(2, "0")}:00`,
                total: 0,
                ...counts,
            };
        });

        events.forEach((event) => {
            const hour = Number.isFinite(event?.hour) ? event.hour : new Date(event.timestamp || Date.now()).getHours();
            if (hour >= 0 && hour < 24) {
                buckets[hour].total += 1;
                if (event?.action && Object.prototype.hasOwnProperty.call(buckets[hour], event.action)) {
                    buckets[hour][event.action] += 1;
                }
            }
        });

        return buckets;
    }
}
